import { FC, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical, faCircleUser } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  apiLogoutUser,
  apiGetMyUnreadNotifications,
  apiCreateUser,
  apiLogUser,
} from '../ui_components/ApiList';
import LoginModale from './LoginModale';
import RegistrazioneModale from './RegistrazioneModale';
import ForgotPasswordModale from './ForgotPasswordModale';
import MenuUserMobile from './MenuUserMobile';
import colors from '../../styles/colors';
import type { User, LoginCredentials, RegisterPayload } from '../../types';

const POLL_UNREAD_INTERVAL_MS = 30000;

const AvatarBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  cursor: pointer;

  & .avatar-icon {
    font-size: 32px;
    color: white;
  }

  & .avatar-icon:hover {
    color: gold;
  }
`;

const UnreadBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 8px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  color: white;
  background-color: ${colors.bgColorRed};
  border-radius: 50%;
  border: 1px solid white;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 80px;
  box-sizing: border-box;
  z-index: 999;
`;

const OverlayPanel = styled.div`
  background: ${colors.bgColorBlack2};
  border: 2px solid ${colors.bgColorBordeaux};
  border-radius: 8px;
  padding: 20px;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  & button.overlay-btn {
    padding: 12px 20px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 6px;
    border: 2px solid ${colors.bgColorBordeaux};
    background: transparent;
    color: white;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
  }

  & button.overlay-btn:hover {
    background: ${colors.bgColorBordeaux};
    color: white;
  }
`;

interface MobileHeaderRightProps {
  user: User | null;
}

const MobileHeaderRight: FC<MobileHeaderRightProps> = ({ user }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoginVisible, setIsLoginVisible] = useState(false);
  const [isRegistrazioneVisible, setIsRegistrazioneVisible] = useState(false);
  const [isForgotPasswordVisible, setIsForgotPasswordVisible] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiGetMyUnreadNotifications();
      setUnreadCount(res.data?.length ?? 0);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_UNREAD_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, fetchUnreadCount]);

  const handleAvatarClick = () => {
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  const handleLogOutClick = async () => {
    try {
      await apiLogoutUser();
      dispatch(logout());
      setMenuOpen(false);
      if (location.pathname.startsWith('/admin')) {
        navigate('/');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleOpenLogin = () => {
    setMenuOpen(false);
    setIsLoginVisible(true);
  };

  const handleOpenRegistrazione = () => {
    setMenuOpen(false);
    setIsRegistrazioneVisible(true);
  };

  const closeLogin = () => setIsLoginVisible(false);
  const closeRegistrazione = () => setIsRegistrazioneVisible(false);
  const openRegistrazioneFromLogin = () => {
    setIsLoginVisible(false);
    setIsRegistrazioneVisible(true);
  };
  const openForgotPasswordFromLogin = () => {
    setIsLoginVisible(false);
    setIsForgotPasswordVisible(true);
  };
  const openLoginFromForgotPassword = () => {
    setIsForgotPasswordVisible(false);
    setIsLoginVisible(true);
  };

  const createNewUser = async (userData: RegisterPayload): Promise<unknown> => {
    return apiCreateUser(userData);
  };

  const logUser = async (userData: LoginCredentials): Promise<unknown> => {
    return apiLogUser(userData);
  };

  if (!user) {
    return (
      <>
        <AvatarBox onClick={handleAvatarClick} role="button" aria-label="Menu utente">
          <FontAwesomeIcon icon={faEllipsisVertical} className="avatar-icon" />
        </AvatarBox>
        {menuOpen && (
          <Overlay onClick={handleCloseMenu}>
            <OverlayPanel onClick={(e) => e.stopPropagation()}>
              <button type="button" className="overlay-btn" onClick={handleOpenLogin}>
                Login
              </button>
              <button type="button" className="overlay-btn" onClick={handleOpenRegistrazione}>
                Registrati
              </button>
              <button type="button" className="overlay-btn" onClick={handleCloseMenu}>
                Chiudi
              </button>
            </OverlayPanel>
          </Overlay>
        )}
        {isLoginVisible && (
          <LoginModale
            closeModalClick={closeLogin}
            closeLoginOpenRegistrazione={openRegistrazioneFromLogin}
            openForgotPassword={openForgotPasswordFromLogin}
            logUser={logUser}
          />
        )}
        {isForgotPasswordVisible && (
          <ForgotPasswordModale
            closeModalClick={() => setIsForgotPasswordVisible(false)}
            openLogin={openLoginFromForgotPassword}
          />
        )}
        {isRegistrazioneVisible && (
          <RegistrazioneModale closeModalClick={closeRegistrazione} createNewUser={createNewUser} />
        )}
      </>
    );
  }

  return (
    <>
      <AvatarBox onClick={handleAvatarClick} role="button" aria-label="Menu utente">
        <FontAwesomeIcon icon={faCircleUser} className="avatar-icon" />
        {unreadCount > 0 && (
          <UnreadBadge title={`${unreadCount} notifiche non lette`}>{unreadCount}</UnreadBadge>
        )}
      </AvatarBox>
      {menuOpen && (
        <MenuUserMobile
          user={user}
          handleVisiblityMenuMobile={handleCloseMenu}
          handleLogOutClick={handleLogOutClick}
          unreadNotificationsCount={unreadCount}
          onNotificationsRead={fetchUnreadCount}
        />
      )}
    </>
  );
};

export default MobileHeaderRight;
